import { useContext } from 'react';

import { useAddCollectionItem } from './useAddCollectionItem';
import { useCollection } from './useCollection';
import { useCollectionsList } from './useCollectionsList';
import { useCreateCollection } from './useCreateCollection';
import { useDeleteCollectionItems } from './useDeleteCollectionItems';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { ApiRequestProps } from '../../../common/interface';
import { BOOKMARKS_DESIGNATION, COLLECTIONS_PROVIDER } from '../constants';
import { getBookmarksCollectionId } from '../utils';

export interface BookmarkControllerProps extends ApiRequestProps {
  id: string;
  provider?: string;
  userId?: string;
  contentID: string;
  contentTitle: string;
}

export const useBookmarkController = (params: BookmarkControllerProps) => {
  const { isUserLoading, userProfile } = useContext(LoginContext);
  const { baseURL, id, contentID, contentTitle } = params;
  const collectionId = getBookmarksCollectionId();

  const { data: userCollectionsData } = useCollectionsList({
    baseURL,
    userId: userProfile?.sub,
    enabled: !!userProfile?.sub,
  });

  const { data: bookmarkData, isLoading: isBookmarkLoading } = useCollection({
    baseURL,
    id,
    designation: BOOKMARKS_DESIGNATION,
    enabled: !!userProfile?.sub,
  });

  const { mutateAsync: createCollection } = useCreateCollection({
    baseURL,
    params: {
      designation: BOOKMARKS_DESIGNATION,
      userId: userProfile?.sub,
    },
  });

  const {
    mutateAsync: applyBookmark,
    isPending: applyIsLoading,
    isError: applyIsError,
    reset: applyReset,
  } = useAddCollectionItem({
    id,
    provider: COLLECTIONS_PROVIDER,
    baseURL,
  });

  const {
    mutateAsync: unapplyBookmark,
    isPending: unapplyIsLoading,
    isError: unapplyIsError,
    reset: unapplyReset,
  } = useDeleteCollectionItems({
    id,
    provider: COLLECTIONS_PROVIDER,
    baseURL,
  });

  const isBookmarked = Boolean(
    bookmarkData?.collection_items?.find(collectionItem => {
      return collectionItem.item_key === contentID;
    })
  );

  const bookmarkCollection = userCollectionsData?.find(collection => {
    return collection.id === collectionId;
  });

  const isLoading =
    isUserLoading ||
    applyIsLoading ||
    unapplyIsLoading ||
    isBookmarkLoading ||
    bookmarkData?.isOptimistic;

  const toggleBookmark = async () => {
    if (isBookmarked) {
      return unapplyBookmark([contentID]);
    }

    if (!bookmarkCollection) {
      await createCollection({
        title: 'Bookmarks',
        creator_id: userProfile?.sub || '',
        designation: BOOKMARKS_DESIGNATION,
      });
    }

    return applyBookmark([
      {
        item_key: contentID,
        item_title: contentTitle,
        item_source: 'AU',
        description: '',
      },
    ]);
  };

  return {
    isLoading,
    toggleBookmark,
    isBookmarked,
    isError: applyIsError || unapplyIsError,
    reset: () => {
      unapplyReset();
      applyReset();
    },
  };
};
